import { useState, useEffect } from 'react';
import { PlayCircle, X, Video as VideoIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  const regexps = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/,
    /youtube\.com\/embed\/([^?]+)/,
    /youtube\.com\/v\/([^?]+)/,
  ];
  for (const re of regexps) {
    const match = url.match(re);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
  }
  return null;
}

function getYouTubeThumbnail(url) {
  const embedUrl = getYouTubeEmbedUrl(url);
  if (!embedUrl) return null;
  const idMatch = embedUrl.match(/embed\/([^?]+)/);
  if (idMatch) return `https://img.youtube.com/vi/${idMatch[1]}/hqdefault.jpg`;
  return null;
}

export default function VideoGaleri() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/videos`)
      .then(r => r.json())
      .then(data => { setVideos(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const openVideo = (video) => setActiveVideo(video);
  const closeVideo = () => setActiveVideo(null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amber-50">
        <div className="text-amber-700 text-lg animate-pulse">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl mb-4 shadow-lg">
            <VideoIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-amber-900 mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
            Video Galeri
          </h1>
          <p className="text-lg text-amber-700">Köyümüze ait videoları izleyin</p>
        </div>

        {videos.length === 0 ? (
          <div className="text-center py-16 text-amber-600">
            <VideoIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-xl">Henüz video eklenmemiş.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map(video => {
              const thumbnailUrl = video.thumbnail_url ||
                (video.video_type === 'youtube' ? getYouTubeThumbnail(video.url) : null);

              return (
                <div
                  key={video.id}
                  className="group bg-white rounded-2xl overflow-hidden shadow-md border border-amber-100 hover:shadow-xl hover:border-amber-300 transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
                  onClick={() => openVideo(video)}
                  data-testid={`video-card-${video.id}`}
                >
                  <div className="relative h-48 bg-gray-900">
                    {thumbnailUrl ? (
                      <img src={thumbnailUrl} alt={video.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-amber-700 to-orange-800 flex items-center justify-center">
                        <VideoIcon className="w-16 h-16 text-white opacity-50" />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                        <PlayCircle className="w-12 h-12 text-white drop-shadow-lg" />
                      </div>
                    </div>
                    <div className="absolute top-2 right-2">
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${video.video_type === 'youtube' ? 'bg-red-600 text-white' : 'bg-amber-600 text-white'}`}>
                        {video.video_type === 'youtube' ? 'YouTube' : 'Video'}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-amber-900 text-base mb-1 line-clamp-2">{video.title}</h3>
                    {video.description && <p className="text-amber-700 text-sm line-clamp-2">{video.description}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Video Modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={closeVideo}
          data-testid="video-modal"
        >
          <div
            className="relative w-full max-w-4xl bg-black rounded-2xl overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={closeVideo}
              className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
              data-testid="video-modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            {activeVideo.video_type === 'youtube' ? (
              <div className="relative pt-[56.25%]">
                <iframe
                  src={`${getYouTubeEmbedUrl(activeVideo.url)}?autoplay=1`}
                  title={activeVideo.title}
                  className="absolute inset-0 w-full h-full"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  data-testid="youtube-embed"
                />
              </div>
            ) : (
              <video
                src={activeVideo.url}
                controls
                autoPlay
                className="w-full"
                data-testid="direct-video"
              >
                Tarayıcınız video oynatmayı desteklemiyor.
              </video>
            )}

            <div className="bg-gray-900 p-4">
              <h3 className="text-white font-bold text-lg">{activeVideo.title}</h3>
              {activeVideo.description && <p className="text-gray-400 text-sm mt-1">{activeVideo.description}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
